function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function parseHex(hex: string): { r: number; g: number; b: number } | null {
  if (!hex.startsWith('#')) return null;
  const raw = hex.slice(1);
  if (raw.length === 3) {
    return {
      r: parseInt(raw[0]! + raw[0], 16),
      g: parseInt(raw[1]! + raw[1], 16),
      b: parseInt(raw[2]! + raw[2], 16),
    };
  }
  if (raw.length === 6) {
    return {
      r: parseInt(raw.slice(0, 2), 16),
      g: parseInt(raw.slice(2, 4), 16),
      b: parseInt(raw.slice(4, 6), 16),
    };
  }
  return null;
}

/** Tailwind v4 / shadcn HSL components without the hsl() wrapper, e.g. `161 72% 21%`. */
export function hexToHslComponents(hex: string): string {
  const rgb = parseHex(hex);
  if (!rgb) return '0 0% 50%';

  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

  return `${String(Math.round(h))} ${String(Math.round(s * 100))}% ${String(Math.round(l * 100))}%`;
}

export function hexToRgba(hex: string, alpha: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  return `rgba(${String(rgb.r)}, ${String(rgb.g)}, ${String(rgb.b)}, ${String(alpha)})`;
}

export function hexToHex8(hex: string, alpha: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const a = clamp(Math.round(alpha * 255), 0, 255);
  const ah = a.toString(16).padStart(2, '0');
  return `#${rgb.r.toString(16).padStart(2, '0')}${rgb.g.toString(16).padStart(2, '0')}${rgb.b.toString(16).padStart(2, '0')}${ah}`;
}
